import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import { ArrowDownTrayIcon, CloudArrowUpIcon } from "@heroicons/react/16/solid";
import { useQueryClient } from "@tanstack/react-query";
import { Switch } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useCreateImage } from "../api/mutations";
import Button from "../componentLibrary/Button";
import Modal from "../componentLibrary/Modal";
import Input from "../componentLibrary/Input";
import { useNotification } from "../hooks/useNotification";
import { getRbacErrorMessage } from "../constants/rbacMessages";
import { ImageTypeEnum, ImageI } from "../types";
import Tooltip from "../componentLibrary/Tooltip";
import {
  ALLOWED_IMAGE_ACCEPT_ATTR,
  dataTransferHasFiles,
  INVALID_IMAGE_FILE_MESSAGE,
  isAllowedImageFile,
} from "../utils/imageUploadValidation";

type UploadImageState = {
  image: File | null;
  name: string;
  alt: string;
  type: ImageTypeEnum;
};

export default function AddImageModal({
  isModalOpen,
  setIsModalOpen,
  useUploadedImage,
  showPrefill = false,
  type,
}: {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  useUploadedImage?: (image: ImageI) => void;
  type: ImageTypeEnum;
  showPrefill?: boolean;
}) {
  const { notify } = useNotification();
  const blankImage: UploadImageState = {
    image: null,
    name: "",
    alt: "",
    type,
  };

  const [error, setError] = useState("");
  const [fileData, setFileData] = useState<UploadImageState>(blankImage);
  const [prefill, setPrefill] = useState(false);
  const [isFileDragOver, setIsFileDragOver] = useState(false);

  useEffect(() => {
    setFileData((prev) => ({
      ...prev,
      type,
    }));
  }, [type]);

  const queryClient = useQueryClient();

  const mutation = useCreateImage({
    onSuccess: async (successResponse: ImageI) => {
      if (prefill && useUploadedImage) {
        useUploadedImage(successResponse);
      }
      setIsModalOpen(false);
      notify("success", "The image was uploaded successfully.");
      setFileData(blankImage);
      await queryClient.invalidateQueries({ queryKey: ["images"] });
    },
    onError: async (error) => {
      const errorMsg = getRbacErrorMessage(error);
      setError(errorMsg);
      notify("error", errorMsg);
    },
  });

  const applyPickedFile = (selectedFile: File | undefined) => {
    setError("");
    if (!selectedFile) return;
    if (isAllowedImageFile(selectedFile)) {
      setFileData((prev) => ({ ...prev, image: selectedFile }));
      setError("");
    } else {
      setFileData((prev) => ({ ...prev, image: null }));
      setError(INVALID_IMAGE_FILE_MESSAGE);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    applyPickedFile(event.target.files?.[0]);
  };

  const clearFileDragOver = () => setIsFileDragOver(false);

  const handleUploadZoneDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dataTransferHasFiles(e.dataTransfer)) return;
    setIsFileDragOver(true);
  };

  const handleUploadZoneDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    clearFileDragOver();
  };

  const handleUploadZoneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dataTransferHasFiles(e.dataTransfer)) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleUploadZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearFileDragOver();
    const dropped = e.dataTransfer.files?.[0];
    applyPickedFile(dropped);
  };

  const handleSubmit = () => {
    if (mutation.isPending) return;
    if (!fileData.image || !fileData.name || !fileData.alt || !fileData.type) {
      setError("All fields are required.");
      return;
    }

    const formData = new FormData();
    formData.append("image", fileData.image);
    formData.append("name", fileData.name);
    formData.append("alt", fileData.alt);
    formData.append("type", fileData.type);
    mutation.mutate(formData);
  };

  return (
    <>
      <Modal
        title={`Upload ${type}`}
        size="md"
        isOpen={isModalOpen}
        onCloseModal={() => {
          clearFileDragOver();
          setFileData({
            image: null,
            name: "",
            alt: "",
            type,
          });
          setIsModalOpen(false);
          setError("");
        }}
        height="fit-content"
      >
        <div className="space-y-2">
          <label
            className={`flex items-center justify-center h-16 w-16 bg-white border-2 border-dashed cursor-pointer hover:bg-gray-50 ${
              isFileDragOver ? "border-blue-500 bg-blue-50" : "border-gray-300"
            }`}
            onDragEnter={handleUploadZoneDragEnter}
            onDragLeave={handleUploadZoneDragLeave}
            onDragOver={handleUploadZoneDragOver}
            onDrop={handleUploadZoneDrop}
          >
            <input
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept={ALLOWED_IMAGE_ACCEPT_ATTR}
            />
            {fileData.image ? (
              <img
                src={URL.createObjectURL(fileData.image)}
                alt="File Preview"
                className="object-cover w-full h-full"
              />
            ) : (
              <ArrowDownTrayIcon className="w-8 h-8 text-gray-500" />
            )}
          </label>
          <Input
            label={`${type} name`}
            placeholder="Grief Balloon"
            value={fileData.name}
            onChange={(event) => {
              setError("");
              setFileData((prev) => ({ ...prev, name: event.target.value }));
            }}
          />
          <Input
            label="Alt text"
            placeholder="A floating green balloon"
            value={fileData.alt}
            onChange={(event) => {
              setError("");
              setFileData((prev) => ({
                ...prev,
                alt: event.target.value,
              }));
            }}
          />
          {showPrefill && (
            <div className="flex content-center justify-between">
              <div className="flex flex-row space-x-1 items-center">
                <div className="font-bold">Prefill</div>
                <Tooltip
                  icon={
                    <InformationCircleIcon className="h-4 w-4 text-gray-600" />
                  }
                  text="Prefill the image details in the Image Component after image is uploaded."
                  position="right"
                  isActive={true}
                  maxWidth={160}
                />
              </div>
              <Switch
                checked={prefill}
                onChange={() => setPrefill((prev) => !prev)}
                className="group inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition data-[checked]:bg-blue-600"
              >
                <span className="size-4 translate-x-1 rounded-full bg-white transition group-data-[checked]:translate-x-6" />
              </Switch>
            </div>
          )}
        </div>
        <Button
          className="w-full mt-6"
          onClick={handleSubmit}
          loading={mutation.isPending}
          disabled={!fileData.image || !fileData.name || !fileData.alt}
        >
          <CloudArrowUpIcon className="w-6 h-6 mr-2" /> Upload
        </Button>
        {error && <p className="text-red-500">{error}</p>}
      </Modal>
    </>
  );
}
